import type { UserRole } from '@beta-assur/shared';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ApiClient, Session } from './api';

// Session du portail (BA-021) : restaurée au chargement par le cookie de rafraîchissement,
// perdue dès qu'un rafraîchissement échoue. Les droits affichés suivent ceux de l'API.

export type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'anonymous'; user: null }
  | { status: 'authenticated'; user: Session['user'] };

export interface Auth {
  state: AuthState;
  api: ApiClient;
  login: ApiClient['login'];
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
}

/** Correspondance avec les `requireRole` de l'API. */
export const PERMISSIONS = {
  /** Paramètres, référentiels, utilisateurs, scénarios, modèles, mode d'envoi. */
  administer: ['admin'],
  /** Assurés, contrats, imports. */
  editPortfolio: ['admin', 'manager'],
  /** Journal d'audit. */
  readAudit: ['admin', 'manager'],
  /** Encaissement en agence et journal de caisse (agent : son portefeuille, BA-073). */
  recordPayment: ['admin', 'manager', 'agent'],
} as const satisfies Record<string, readonly UserRole[]>;
export type Permission = keyof typeof PERMISSIONS;

export function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  return role !== undefined && (PERMISSIONS[permission] as readonly UserRole[]).includes(role);
}

const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ api, children }: { api: ApiClient; children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });

  useEffect(() => {
    const stop = api.onSession((session) =>
      setState(
        session
          ? { status: 'authenticated', user: session.user }
          : { status: 'anonymous', user: null },
      ),
    );
    void api.refresh();
    return stop;
  }, [api]);

  const auth = useMemo<Auth>(
    () => ({
      state,
      api,
      login: (input) => api.login(input),
      logout: () => api.logout(),
      can: (permission) => hasPermission(state.user?.role, permission),
    }),
    [api, state],
  );
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error('useAuth hors de AuthProvider');
  return auth;
}

export const useApi = () => useAuth().api;
